
import React, { useState, useEffect } from "react";
import { Modal } from "../../../components/Modal";
import type { SignRequest } from "../../../types/signRequest";
import type { ApprovalRequest } from "../../../types/legalOps";
import { useSignRequestWorkflow } from "../useSignRequestWorkflow";
import { FileUploadKit } from "../../../components/common/FileUploadKit";
import { useAiContext } from "../../../ai/AiContext";
import { useAuth } from "../../../context/AuthContext";
import { ApprovalFlowPanel } from "../../../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../../../hooks/useApprovalFlow";
import type { AttachmentInfo } from "../../../types/legalOps";
import type { AttachmentMeta } from "../../../types/attachments";
import { useToast } from "../../../components/Toast";
import { updateSignRequest } from "../services/signRequestService";
import { uploadApprovalFile, deleteApprovalRequest, updateApprovalRequest } from "../../../services/approvalRequestsService";
import { updateStageDecision } from "../../../services/approvalFlowService"; 
import { isSuperUser } from "../../../config/roles";
// Fix import: Go up 4 levels to root
import { serverTimestamp } from "../../../../firebaseClient";

type UnifiedRequest = SignRequest | ApprovalRequest;

interface Props {
  request: UnifiedRequest;
  onClose: () => void;
  onUpdate: () => void;
}

export function SignRequestDetailModal({ request, onClose, onUpdate }: Props) {
  const { user } = useAuth();
  const { submitToLegal } = useSignRequestWorkflow();

  const { setActiveItem, requestDelete } = useAiContext();
  const [currentRequest, setCurrentRequest] = useState<UnifiedRequest>(request);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useToast();

  const { flow, loading: flowLoading } = useApprovalFlow("SEAL_REQUEST", request.id);
  const isSuper = isSuperUser(user?.email);

  // Field Normalization Helpers
  const reqTitle = currentRequest.title;
  const reqId = currentRequest.id;
  const reqStatus = currentRequest.status;
  const requesterName = (currentRequest as any).requesterName || (currentRequest as any).createdByUserName || "User";
  const reqDeptName = (currentRequest as any).department || (currentRequest as any).createdByDeptName || "Dept";
  const reqCategory = (currentRequest as any).docCategory || (currentRequest as any).category || "一般";
  const reqAmount = (currentRequest as any).contractAmount || (currentRequest as any).amount || 0;
  const reqSourceType = (currentRequest as any).module || (currentRequest as any).sourceType || "Manual";
  const reqDescription = currentRequest.description;
  const reqConsultedDepts = currentRequest.consultedDepts;
  const reqOtherComments = currentRequest.otherDeptComments;
  const reqAttachments = currentRequest.attachments;
  const reqReviewAttachments = currentRequest.reviewAttachments;

  useEffect(() => {
    setCurrentRequest(request);
    setActiveItem({
        type: "signRequest",
        id: request.id,
        title: request.title,
        data: request,
        attachments: request.attachments
    });
    return () => setActiveItem(null);
  }, [request, setActiveItem]);

  const handleDecision = async (
    stageKey: string,
    decision: "APPROVED" | "REJECTED" | "DONE" | "RETURNED",
    comment: string,
    deptCode?: string
  ) => {
      if (!flow) return;
      setIsProcessing(true);
      try {
          const safeDecision = decision as "APPROVED" | "REJECTED" | "RETURNED" | "DONE";
          
          // 1. Update Flow Stage Decision
          await updateStageDecision({
              flowId: flow.id,
              stageKey: stageKey as any,
              decision: safeDecision,
              comment,
              actorUid: user?.id || 'unknown',
              actorName: user?.displayName || 'User',
              deptCode
          });
          
          // 2. Determine New Parent Document Status (Strict Logic)
          // Default: Keep current status (e.g. IN_REVIEW)
          let newStatus = currentRequest.status;
          let completedAt = undefined;

          // --- Status Transition Logic ---
          if (decision === 'REJECTED') {
              // ANY rejection terminates the flow immediately
              newStatus = 'REJECTED'; 
              completedAt = serverTimestamp();
          } else if (decision === 'RETURNED') {
              // ANY return terminates the current flow cycle
              if (stageKey === 'LEGAL_REVIEW') {
                  newStatus = 'LEGAL_RETURNED'; 
              } else {
                  newStatus = 'RETURNED'; 
              }
              completedAt = serverTimestamp();
          } else if (decision === 'APPROVED') {
              // If Last Stage Approved -> Completed
              if (stageKey === 'MANAGER_IRENE') {
                  newStatus = 'FULLY_APPROVED'; // Terminal Success Status
                  completedAt = serverTimestamp();
              } else {
                  // Intermediate Stages Transitions
                  // If Legal Approved -> Manager
                  if (stageKey === 'LEGAL_REVIEW') {
                      newStatus = 'PENDING_MANAGER'; 
                  } else if (stageKey === 'MANAGER_LISA') {
                      newStatus = 'PENDING_MANAGER'; // Still manager phase
                  }
                  // Note: If previously was RETURNED, reviving it makes it PENDING_X
              }
          }

          // 3. Update Document if status changed
          // We force update status if it detects a terminal decision to ensure UI consistency
          // Also force update if it moves from In Review to Pending Manager
          if (newStatus !== currentRequest.status || completedAt) {
              console.log(`[SignRequest] Updating status from ${currentRequest.status} to ${newStatus}`);
              
              const updates: any = { status: newStatus as any };
              if (completedAt) updates.completedAt = completedAt;
              
              // Determine which update service to use based on object type
              if ('module' in currentRequest) {
                  await updateApprovalRequest(currentRequest.id, updates);
              } else {
                  await updateSignRequest(currentRequest.id, updates);
              }
              
              // Update local state for immediate UI feedback
              setCurrentRequest(prev => ({ ...prev, ...updates }));
          }
          
          showToast(`已執行: ${decision === 'APPROVED' ? '核准' : decision === 'REJECTED' ? '駁回' : '退回'}`);
          
          // 4. Close Modal and Refresh Parent
          setTimeout(() => {
              onUpdate(); 
          }, 500); 

      } catch(e) {
          console.error(e);
          showToast("操作失敗", "error");
      } finally {
          setIsProcessing(false);
      }
  };

  const handleDelete = () => {
      // Cast to string to support status checks outside strict type definition if data drifts
      const s = currentRequest.status as string;
      if (s === 'APPROVED' || s === 'FULLY_APPROVED' || s === 'PUBLISHED') {
          alert("此案件已完成簽核（或已用印），不可刪除，請改用作廢流程。");
          return;
      }

      requestDelete({
          targetType: 'sealRequest',
          targetId: currentRequest.id,
          summary: `用印申請：${currentRequest.title}`,
          onConfirm: async () => {
              try {
                  await deleteApprovalRequest(currentRequest.id, { uid: user?.id||'unknown', name: user?.displayName||'User' });
                  showToast("已刪除申請單");
                  onClose();
                  onUpdate();
              } catch(e: any) {
                  console.error(e);
                  showToast(e.message || "刪除失敗", "error");
              }
          }
      });
  };

  const statusColor = (s: string) => {
      if(s === 'APPROVED' || s === 'FULLY_APPROVED') return 'var(--ok)';
      if(s === 'REJECTED' || s === 'RETURNED' || s === 'LEGAL_RETURNED') return 'var(--danger)';
      if(s === 'PENDING_MANAGER') return '#8b5cf6';
      if(s === 'IN_REVIEW') return '#fbbf24';
      return '#aaa';
  };

  const statusLabel = (s: string) => {
      const map: Record<string, string> = {
          'IN_REVIEW': '審查中 (法務)',
          'PENDING_MANAGER': '待主管核決',
          'APPROVED': '已核准 (流程完成)',
          'FULLY_APPROVED': '已核准 (流程完成)',
          'REJECTED': '已駁回',
          'RETURNED': '已退回',
          'LEGAL_RETURNED': '法務退回',
          'DRAFT': '草稿',
          'CANCELLED': '已作廢'
      };
      return map[s] || s;
  };

  // Determine if editable actions should be shown
  // Hide actions if terminal status
  const isTerminal = ['APPROVED', 'FULLY_APPROVED', 'REJECTED', 'RETURNED', 'LEGAL_RETURNED', 'CANCELLED'].includes(currentRequest.status);

  return (
    <Modal title={`簽呈詳情: ${reqTitle}`} onClose={onClose} width={1100}>
       <div style={{display:'grid', gridTemplateColumns:'1fr 400px', gap: 24}}>
          {/* Left: Request Form Data (Read-only mostly) */}
          <div>
             <div style={{marginBottom:16, display:'flex', gap:10, alignItems:'center'}}>
                <span className="badge" style={{color: statusColor(reqStatus), borderColor: statusColor(reqStatus), fontSize:14}}>
                    {statusLabel(reqStatus)}
                </span>
                <span style={{fontSize:13, color:'var(--muted)'}}>單號: {reqId}</span>
             </div>
             
             <div className="section" style={{padding:16, marginBottom:16}}>
                <div className="p" style={{color:'#fff', fontWeight:'bold', fontSize:16, marginBottom:8}}>{reqTitle}</div>
                <div className="p">類別: {reqCategory} | 金額: {reqAmount || 0}</div>
                <div className="p">申請人: {requesterName} ({reqDeptName})</div>
                
                {/* Linked Codes */}
                <div style={{marginTop:8, paddingTop:8, borderTop:'1px dashed #333', fontSize:12}}>
                    {(currentRequest as any).reviewCode && <div style={{color:'#a5b4fc'}}>會審編號: {(currentRequest as any).reviewCode}</div>}
                    {(currentRequest as any).archiveCode && <div style={{color:'#34d399'}}>歸檔編號: {(currentRequest as any).archiveCode}</div>}
                </div>

                {reqConsultedDepts && (
                    <div className="p" style={{marginTop:8, color:'#a5b4fc'}}>已徵詢部門：{reqConsultedDepts}</div>
                )}
                
                <div className="p" style={{marginTop:12, fontWeight:500, color:'#ccc'}}>說明/擬辦：</div>
                <div style={{marginTop:4, background:'rgba(255,255,255,0.05)', padding:12, borderRadius:6, whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.5, color:'#ddd'}}>
                    {reqDescription}
                </div>

                {reqOtherComments && (
                    <div style={{marginTop:12}}>
                        <div className="p" style={{fontWeight:500, color:'#ccc'}}>其他部門意見摘要：</div>
                        <div style={{marginTop:4, background:'rgba(255,255,255,0.05)', padding:12, borderRadius:6, whiteSpace:'pre-wrap', fontSize:14, lineHeight:1.5, color:'#ddd'}}>
                            {reqOtherComments}
                        </div>
                    </div>
                )}

                {reqAttachments && reqAttachments.length > 0 && (
                    <div style={{marginTop:16}}>
                       <FileUploadKit existingFiles={reqAttachments} disabled />
                    </div>
                )}
             </div>
          </div>

          {/* Right: Approval Flow Panel */}
          <div style={{borderLeft:'1px solid #333', paddingLeft:24}}>
             <h4 className="h1" style={{color:'#8b5cf6', marginBottom:16}}>簽核進度</h4>
             
             {reqStatus === 'DRAFT' && (
                 <div style={{marginBottom: 20}}>
                     <div className="p" style={{marginBottom:10}}>請確認內容無誤後送出簽核。</div>
                     <div className="p" style={{color:'orange'}}>請在列表頁點擊「送出」或使用上方按鈕啟動流程。</div>
                 </div>
             )}

             {isTerminal && (
                 <div style={{marginBottom: 16, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 6, color: '#aaa', fontSize: 13}}>
                     ℹ️ 此案件已結案或終止，無法再進行簽核操作。
                 </div>
             )}

             {flowLoading ? <div>Loading flow...</div> : (
                <div style={{pointerEvents: isTerminal ? 'none' : 'auto', opacity: isTerminal ? 0.8 : 1}}>
                    <ApprovalFlowPanel 
                        flow={flow} 
                        onDecision={handleDecision}
                        requestId={reqId}
                        reviewAttachments={reqReviewAttachments} 
                    />
                </div>
             )}
          </div>
       </div>
       <div className="row" style={{marginTop:20, justifyContent:'space-between'}}>
          <div>
              {isSuper && (
                  <button 
                    className="btn" 
                    style={{width:'auto', background:'transparent', border:'1px solid var(--danger)', color:'var(--danger)'}} 
                    onClick={handleDelete}
                  >
                    刪除此單 (SuperUser)
                  </button>
              )}
          </div>
          <button className="btn" style={{width:'auto'}} onClick={onClose}>關閉</button>
       </div>
    </Modal>
  );
}
